import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { metricsApi, type DailyAgg, type MonthlySummaryRow } from "@/api/metrics";
import { fmt, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

function shiftMonth(monthIso: string, delta: -1 | 1): string {
  const [y, m] = monthIso.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function thisMonthIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthBounds(monthIso: string): { from: string; to: string; days: string[] } {
  const [year, month] = monthIso.split("-").map(Number);
  const first = new Date(Date.UTC(year, month - 1, 1));
  const last = new Date(Date.UTC(year, month, 0));
  const days: string[] = [];
  for (let d = 1; d <= last.getUTCDate(); d++) {
    days.push(`${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }
  return {
    from: first.toISOString().slice(0, 10),
    to: last.toISOString().slice(0, 10),
    days,
  };
}

interface DerivedMetrics {
  // Прямі поля + обчислені.
  fact_revenue: number;
  plan_revenue: number;
  rev_pct: number | null;
  fact_profit: number;
  plan_profit: number;
  prof_pct: number | null;
  fact_kwh: number;
  self_needs: number;
  imbalance_pos: number;
  imbalance_neg: number;
  fact_cost: number;
  fact_per_mwh: number | null;
  avg_power_kw: number;
  fact_hours: number;
}

function derive(r: DailyAgg | null): DerivedMetrics {
  const fact_revenue = num(r?.fact_revenue_uah) ?? 0;
  const plan_revenue = num(r?.plan_revenue_uah) ?? 0;
  const fact_profit = num(r?.fact_profit_uah) ?? 0;
  const plan_profit = num(r?.plan_profit_uah) ?? 0;
  const fact_kwh = num(r?.fact_generation_kwh) ?? 0;
  const fact_hours = r?.fact_hours ?? 0;
  const fact_cost = fact_revenue - fact_profit;
  return {
    fact_revenue,
    plan_revenue,
    rev_pct: plan_revenue > 0 ? (fact_revenue / plan_revenue) * 100 : null,
    fact_profit,
    plan_profit,
    prof_pct: plan_profit > 0 ? (fact_profit / plan_profit) * 100 : null,
    fact_kwh,
    self_needs: num(r?.fact_self_needs_kwh) ?? 0,
    imbalance_pos: num(r?.imbalance_pos_kwh) ?? 0,
    imbalance_neg: num(r?.imbalance_neg_kwh) ?? 0,
    fact_cost,
    fact_per_mwh: fact_kwh > 0 ? fact_cost / (fact_kwh / 1000) : null,
    // Середня потужність = Σ kWh / Σ заповнених годин (а не /24), щоб
    // дірки в metrics_hourly не занижували значення.
    avg_power_kw: fact_hours > 0 ? fact_kwh / fact_hours : 0,
    fact_hours,
  };
}

function pctColor(pct: number | null): string {
  if (pct === null) return "";
  if (pct >= 95 && pct <= 105) return "text-emerald-700";
  if (pct >= 85 && pct < 95) return "text-amber-700";
  if (pct > 105 && pct <= 115) return "text-amber-700";
  return "text-red-700";
}

export function MonthlyPage() {
  const { t } = useTranslation();
  const [monthIso, setMonthIso] = useState(thisMonthIso());
  const { from, to, days } = useMemo(() => monthBounds(monthIso), [monthIso]);

  const daily = useQuery({
    queryKey: ["metrics-daily", from, to],
    queryFn: () => metricsApi.daily(from, to),
  });
  const summaryQ = useQuery({
    queryKey: ["metrics-monthly-summary", monthIso],
    queryFn: () => metricsApi.monthlySummary(monthIso),
    retry: 0,
  });

  const byDay = useMemo(() => {
    const m = new Map<string, DailyAgg>();
    (daily.data ?? []).forEach((r) => m.set(r.day.slice(0, 10), r));
    return m;
  }, [daily.data]);

  const rows = useMemo(
    () => days.map((d) => ({ day: d, raw: byDay.get(d) ?? null, m: derive(byDay.get(d) ?? null) })),
    [days, byDay],
  );

  const totals = useMemo(() => {
    const t: DerivedMetrics = {
      fact_revenue: 0,
      plan_revenue: 0,
      rev_pct: null,
      fact_profit: 0,
      plan_profit: 0,
      prof_pct: null,
      fact_kwh: 0,
      self_needs: 0,
      imbalance_pos: 0,
      imbalance_neg: 0,
      fact_cost: 0,
      fact_per_mwh: 0,
      avg_power_kw: 0,
      fact_hours: 0,
    };
    for (const { m } of rows) {
      if (!m) continue;
      t.fact_revenue += m.fact_revenue;
      t.plan_revenue += m.plan_revenue;
      t.fact_profit += m.fact_profit;
      t.plan_profit += m.plan_profit;
      t.fact_kwh += m.fact_kwh;
      t.self_needs += m.self_needs;
      t.imbalance_pos += m.imbalance_pos;
      t.imbalance_neg += m.imbalance_neg;
      t.fact_cost += m.fact_cost;
      t.fact_hours += m.fact_hours;
    }
    t.rev_pct = t.plan_revenue > 0 ? (t.fact_revenue / t.plan_revenue) * 100 : null;
    t.prof_pct = t.plan_profit > 0 ? (t.fact_profit / t.plan_profit) * 100 : null;
    t.fact_per_mwh = t.fact_kwh > 0 ? t.fact_cost / (t.fact_kwh / 1000) : null;
    t.avg_power_kw = t.fact_hours > 0 ? t.fact_kwh / t.fact_hours : 0;
    return t;
  }, [rows]);

  // Heatmap intensity: profit per day relative to max abs profit.
  const maxAbsProfit = useMemo(() => {
    return Math.max(0.01, ...rows.map((r) => Math.abs(r.m.fact_profit)));
  }, [rows]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("monthly.title")}</CardTitle>
          <CardDescription>{t("monthly.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 w-fit">
            <Label>{t("monthly.month")}</Label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMonthIso((m) => shiftMonth(m, -1))}
                aria-label={t("home1.prev_day")}
                title={t("home1.prev_day")}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input
                type="month"
                value={monthIso}
                onChange={(e) => setMonthIso(e.target.value)}
                className="w-[180px]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMonthIso((m) => shiftMonth(m, 1))}
                aria-label={t("home1.next_day")}
                title={t("home1.next_day")}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Грід: heatmap зліва, KPI справа. На вузьких — один під одним. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("monthly.heatmap_title")}</CardTitle>
            <CardDescription>{t("monthly.heatmap_subtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1 max-w-[420px]">
              {rows.map(({ day, m }) => {
                const dayNum = Number(day.slice(8, 10));
                const intensity = m.fact_profit / maxAbsProfit;
                const bg =
                  m.fact_profit > 0
                    ? `rgba(16, 185, 129, ${Math.max(0.05, intensity).toFixed(2)})`
                    : m.fact_profit < 0
                      ? `rgba(239, 68, 68, ${Math.max(0.05, Math.abs(intensity)).toFixed(2)})`
                      : "rgb(241, 245, 249)";
                return (
                  <div
                    key={day}
                    title={`${day}: ${fmt(m.fact_profit)} грн`}
                    className="aspect-square rounded text-xs flex items-center justify-center font-mono"
                    style={{ backgroundColor: bg }}
                  >
                    {dayNum}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* KPI блок: 6 ключових показників за місяць */}
        <KpiBlock totals={totals} summary={summaryQ.data ?? null} />
      </div>

      {/* Зведена таблиця: PLAN / FACT / % */}
      <MonthlySummaryTable summary={summaryQ.data ?? null} loading={summaryQ.isLoading} />

      <Card>
        <CardContent className="p-0">
          {daily.isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="p-2">{t("monthly.day")}</th>
                    <th className="p-2 text-right">{t("monthly.fact_kwh")}</th>
                    <th className="p-2 text-right">{t("monthly.avg_power")}</th>
                    <th className="p-2 text-right">{t("monthly.plan_revenue")}</th>
                    <th className="p-2 text-right">{t("monthly.fact_revenue")}</th>
                    <th className="p-2 text-right">{t("monthly.rev_pct")}</th>
                    <th className="p-2 text-right">{t("monthly.plan_profit")}</th>
                    <th className="p-2 text-right">{t("monthly.fact_profit")}</th>
                    <th className="p-2 text-right">{t("monthly.prof_pct")}</th>
                    <th className="p-2 text-right">{t("monthly.fact_cost")}</th>
                    <th className="p-2 text-right">{t("monthly.cost_per_mwh")}</th>
                    <th className="p-2 text-right">{t("monthly.self_needs")}</th>
                    <th className="p-2 text-right">{t("monthly.imbalance_pos")}</th>
                    <th className="p-2 text-right">{t("monthly.imbalance_neg")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ day, m }) => (
                    <tr key={day} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-2 font-mono text-xs">{day.slice(8, 10)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.fact_kwh)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.avg_power_kw)}</td>
                      <td className="p-2 text-right font-mono text-slate-500">{fmt(m.plan_revenue)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.fact_revenue)}</td>
                      <td className={cn("p-2 text-right font-mono", pctColor(m.rev_pct))}>
                        {m.rev_pct === null ? "—" : `${fmt(m.rev_pct, 1)}%`}
                      </td>
                      <td className="p-2 text-right font-mono text-slate-500">{fmt(m.plan_profit)}</td>
                      <td
                        className={cn(
                          "p-2 text-right font-mono",
                          m.fact_profit < 0 ? "text-red-700" : "",
                        )}
                      >
                        {fmt(m.fact_profit)}
                      </td>
                      <td className={cn("p-2 text-right font-mono", pctColor(m.prof_pct))}>
                        {m.prof_pct === null ? "—" : `${fmt(m.prof_pct, 1)}%`}
                      </td>
                      <td className="p-2 text-right font-mono">{fmt(m.fact_cost)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.fact_per_mwh)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.self_needs)}</td>
                      <td className="p-2 text-right font-mono text-emerald-700">
                        {m.imbalance_pos > 0 ? "+" + fmt(m.imbalance_pos) : ""}
                      </td>
                      <td className="p-2 text-right font-mono text-red-700">
                        {m.imbalance_neg < 0 ? fmt(m.imbalance_neg) : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold">
                  <tr>
                    <td className="p-2">{t("common.total")}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.fact_kwh)}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.avg_power_kw)}</td>
                    <td className="p-2 text-right font-mono text-slate-500">{fmt(totals.plan_revenue)}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.fact_revenue)}</td>
                    <td className={cn("p-2 text-right font-mono", pctColor(totals.rev_pct))}>
                      {totals.rev_pct === null ? "—" : `${fmt(totals.rev_pct, 1)}%`}
                    </td>
                    <td className="p-2 text-right font-mono text-slate-500">{fmt(totals.plan_profit)}</td>
                    <td
                      className={cn(
                        "p-2 text-right font-mono",
                        totals.fact_profit < 0 ? "text-red-700" : "",
                      )}
                    >
                      {fmt(totals.fact_profit)}
                    </td>
                    <td className={cn("p-2 text-right font-mono", pctColor(totals.prof_pct))}>
                      {totals.prof_pct === null ? "—" : `${fmt(totals.prof_pct, 1)}%`}
                    </td>
                    <td className="p-2 text-right font-mono">{fmt(totals.fact_cost)}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.fact_per_mwh)}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.self_needs)}</td>
                    <td className="p-2 text-right font-mono text-emerald-700">
                      {totals.imbalance_pos > 0 ? "+" + fmt(totals.imbalance_pos) : ""}
                    </td>
                    <td className="p-2 text-right font-mono text-red-700">
                      {totals.imbalance_neg < 0 ? fmt(totals.imbalance_neg) : ""}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── KPI block (heatmap-side) ───

import type { MonthlySummary } from "@/api/metrics";

function KpiBlock({
  totals,
  summary,
}: {
  totals: DerivedMetrics;
  summary: MonthlySummary | null;
}) {
  const { t } = useTranslation();
  const fact = summary?.rows.find((r) => r.label === "fact");
  const plan = summary?.rows.find((r) => r.label === "plan");
  const pct = summary?.rows.find((r) => r.label === "pct");

  const factCostPerMwh = totals.fact_per_mwh;
  const factAvgPower = totals.avg_power_kw;
  const factProfit = totals.fact_profit;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("monthly.kpi_title")}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3">
        <KpiCell
          label={t("monthly.cost_per_mwh")}
          fact={fmt(factCostPerMwh)}
          plan={null}
          pctValue={null}
        />
        <KpiCell
          label={t("monthly.avg_power")}
          fact={fmt(factAvgPower)}
          plan={null}
          pctValue={null}
        />
        <KpiCell
          label={t("home1.imbalance_pos")}
          fact={fact?.imbalance_pos_kwh != null ? "+" + fmt(num(fact.imbalance_pos_kwh)) : "—"}
          plan={null}
          pctValue={null}
          tone="emerald"
        />
        <KpiCell
          label={t("home1.imbalance_neg")}
          fact={fact?.imbalance_neg_kwh != null ? fmt(num(fact.imbalance_neg_kwh)) : "—"}
          plan={null}
          pctValue={null}
          tone="red"
        />
        <KpiCell
          label={t("monthly.fact_profit")}
          fact={fmt(factProfit)}
          plan={plan?.profit_total_uah != null ? fmt(num(plan.profit_total_uah)) : null}
          pctValue={pct?.profit_total_uah != null ? num(pct.profit_total_uah) : null}
          tone={factProfit < 0 ? "red" : factProfit > 0 ? "emerald" : undefined}
        />
        <KpiCell
          label={t("monthly.prof_pct")}
          fact={totals.prof_pct !== null ? `${fmt(totals.prof_pct, 1)}%` : "—"}
          plan={null}
          pctValue={null}
        />
      </CardContent>
    </Card>
  );
}

function KpiCell({
  label,
  fact,
  plan,
  pctValue,
  tone,
}: {
  label: string;
  fact: string;
  plan: string | null;
  pctValue: number | null;
  tone?: "red" | "emerald";
}) {
  const pctCls = pctValue === null
    ? ""
    : pctValue >= 95 && pctValue <= 105
      ? "text-emerald-700"
      : "text-red-700";
  return (
    <div className="rounded border border-slate-100 p-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div
        className={cn(
          "text-lg font-mono font-semibold",
          tone === "red" && "text-red-700",
          tone === "emerald" && "text-emerald-700",
        )}
      >
        {fact}
      </div>
      {plan !== null && (
        <div className="text-[10px] text-slate-400 font-mono mt-0.5">
          plan: {plan}
          {pctValue !== null && (
            <span className={cn("ml-1", pctCls)}>({fmt(pctValue, 1)}%)</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Зведена таблиця (PLAN / FACT / %) ───

function MonthlySummaryTable({ summary, loading }: { summary: MonthlySummary | null; loading: boolean }) {
  const { t } = useTranslation();
  if (loading) {
    return <Card><CardContent className="p-6 text-sm text-slate-500">{t("common.loading")}</CardContent></Card>;
  }
  if (!summary) return null;
  const plan = summary.rows.find((r) => r.label === "plan");
  const fact = summary.rows.find((r) => r.label === "fact");
  const pct = summary.rows.find((r) => r.label === "pct");

  type Col = {
    key: keyof MonthlySummaryRow;
    label: string;
    digits?: number;
    signed?: boolean;
  };
  const cols: Col[] = [
    { key: "rdn_price_100pct_uah_mwh", label: t("monthly.rdn_price_100pct"), digits: 2 },
    { key: "generation_mwh", label: t("monthly.generation_mwh"), digits: 2 },
    { key: "rdn_price_avg_net_uah_mwh", label: t("monthly.rdn_price_net"), digits: 2 },
    { key: "volume_rdn_mwh", label: t("monthly.volume_rdn_mwh"), digits: 2 },
    { key: "volume_ekosfera_mwh", label: t("monthly.volume_ekosfera_mwh"), digits: 2 },
    { key: "revenue_rdn_uah", label: t("monthly.revenue_rdn") },
    { key: "profit_rdn_uah", label: t("monthly.profit_rdn"), signed: true },
    { key: "revenue_ekosfera_uah", label: t("monthly.revenue_ekosfera") },
    { key: "profit_ekosfera_uah", label: t("monthly.profit_ekosfera"), signed: true },
    { key: "revenue_total_uah", label: t("monthly.revenue_total") },
    { key: "profit_total_uah", label: t("monthly.profit_total"), signed: true },
    { key: "imbalance_pos_kwh", label: t("home1.imbalance_pos") },
    { key: "imbalance_neg_kwh", label: t("home1.imbalance_neg") },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("monthly.summary_title")}</CardTitle>
        <CardDescription>{t("monthly.summary_subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500 text-xs">
                <th className="p-2"></th>
                {cols.map((c) => <th key={c.key} className="p-2 text-right">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {[
                { row: plan, label: t("monthly.plan_row") },
                { row: fact, label: t("monthly.fact_row") },
                { row: pct,  label: t("monthly.pct_row") },
              ].map(({ row, label }, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="p-2 font-medium">{label}</td>
                  {cols.map((c) => {
                    const v = row?.[c.key];
                    const n = v == null ? null : num(v);
                    const isPct = row?.label === "pct";
                    const cellClass = cn(
                      "p-2 text-right font-mono",
                      c.signed && n !== null && n > 0 && "text-emerald-700",
                      c.signed && n !== null && n < 0 && "text-red-700",
                      isPct && n !== null && n >= 95 && n <= 105 && "text-emerald-700",
                      isPct && n !== null && (n < 95 || n > 105) && "text-red-700",
                    );
                    return (
                      <td key={c.key} className={cellClass}>
                        {n === null ? "—" : isPct ? `${fmt(n, 1)}%` : fmt(n, c.digits ?? 0)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
